import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Chip,
} from "@mui/material";
import sh from "bundles/common/utils/sh";

const CHART_TYPE_ICONS = {
  kpi: "📊",
  bar: "📈",
  pie: "🥧",
  line: "📉",
  doughnut: "🍩",
  area: "📈",
};

const CATEGORY_COLORS = {
  Applicants: "#6366f1",
  Candidates: "#10b981",
  Recruiters: "#f59e0b",
  Companies: "#ec4899",
  Projects: "#8b5cf6",
  Email: "#06b6d4",
  Polls: "#ef4444",
  Automations: "#84cc16",
};

const CustomChartsPicker = ({ selectedKey, onSelect }) => {
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);

  useEffect(() => {
    sh.get("/internal/custom_charts")
      .then((res) => {
        setCharts(res.data);
        setLoading(false);
        if (res.data.length > 0) {
          setExpandedCategory(res.data[0].category);
        }
      })
      .catch(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    return charts.reduce((acc, chart) => {
      if (!acc[chart.category]) acc[chart.category] = [];
      acc[chart.category].push(chart);
      return acc;
    }, {});
  }, [charts]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <Typography variant="body2">Loading charts...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {Object.entries(grouped).map(([category, categoryCharts]) => (
        <Box key={category} sx={{ mb: 2 }}>
          <Box
            onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              mb: 0.5,
              "&:hover": { opacity: 0.8 },
            }}
          >
            <Chip
              label={category}
              size="small"
              sx={{
                backgroundColor: CATEGORY_COLORS[category] || "#6366f1",
                color: "#fff",
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                fontSize: 11,
                height: 22,
              }}
            />
            <Typography variant="caption" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 11, color: "text.secondary" }}>
              {categoryCharts.length} chart{categoryCharts.length !== 1 ? "s" : ""}
            </Typography>
          </Box>

          {expandedCategory === category && (
            <Grid container spacing={1} alignItems="stretch">
              {categoryCharts.map((chart) => (
                <Grid item xs={6} key={chart.key} sx={{ display: "flex" }}>
                  <Card
                    variant="outlined"
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderColor: selectedKey === chart.key ? "primary.main" : "divider",
                      borderWidth: selectedKey === chart.key ? 2 : 1,
                      backgroundColor: selectedKey === chart.key ? "action.selected" : "transparent",
                    }}
                  >
                    <CardActionArea
                      onClick={() => onSelect(chart)}
                      sx={{ p: 1.5, flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "flex-start" }}
                    >
                      <CardContent sx={{ p: "0 !important", "&:last-child": { pb: 0 } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 12 }}>
                            {CHART_TYPE_ICONS[chart.chart_type] || "📊"} {chart.name}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 10, color: "text.secondary" }}>
                          {chart.description}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={chart.chart_type.toUpperCase()}
                            size="small"
                            sx={{ height: 16, fontSize: 9, px: 0.5 }}
                          />
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default CustomChartsPicker;
